import { useState } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import { Tick01Icon } from "@hugeicons/core-free-icons";
import {
  formatPlanBytes,
  PAYG_BYTES_PER_DOLLAR,
  PLANS,
  PLAN_IDS,
} from "@saucer/core";
import type { Plan } from "@saucer/core";
import { go, SaasFooter, SaasNav } from "../components/saas-shell.js";

/**
 * The pricing page.
 *
 * Two honest things shape it. Bandwidth is genuinely unlimited — R2 charges
 * nothing for egress, so the only meter is storage, and saying so plainly is
 * more convincing than a table of asterisks. And there is no payment provider:
 * upgrades are arranged by email and applied by hand, which the page states
 * rather than hides behind a checkout button that would not work.
 */

const UPGRADE_EMAIL = "hey@saucer.sh";

export function Pricing() {
  return (
    <div className="landing saas">
      <SaasNav />

      <section className="price-hero">
        <span className="saas-chip">Pricing</span>
        <h1 className="saas-h2">
          Pay for storage.
          <br />
          Nothing else.
        </h1>
        <p className="saas-lede">
          Bandwidth is unlimited on every plan — a link that goes around the world costs the same
          as one nobody opens. Start free, move up when you run out of room.
        </p>
      </section>

      <section className="price-grid">
        {PLAN_IDS.map((id) => (
          <PlanCard key={id} plan={id} />
        ))}
        <EnterpriseCard />
      </section>

      <Topup />

      <section className="price-note">
        <h2 className="price-note__title">How upgrading works</h2>
        <p>
          There is no checkout here, on purpose. saucer takes no card details and stores none:
          you email <a href={`mailto:${UPGRADE_EMAIL}`}>{UPGRADE_EMAIL}</a> from the address on
          your GitHub account, we settle up however suits you, and your account is moved by hand —
          usually the same day. Your quota changes on your next request.
        </p>
        <p>
          Because it is manual, it is also reversible without ceremony. Stop paying and the
          account falls back to free; nothing is deleted, and every link you have already shared
          keeps working. You simply cannot add anything new until you are back under{" "}
          {formatPlanBytes(PLANS.free.storageBytes)}.
        </p>
      </section>

      <section className="saas-faq" id="pricing-faq">
        <span className="saas-chip">Questions</span>
        <h2 className="saas-h2">The awkward ones</h2>
        <div className="saas-accordion">
          {FAQ.map((item) => (
            <details key={item.q} className="saas-accordion__item">
              <summary>{item.q}</summary>
              <p>{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="saas-cta">
        <div className="saas-wrap-5xl">
          <div className="saas-cta__card">
            <h2 className="saas-cta__title">
              {formatPlanBytes(PLANS.free.storageBytes)} is a
              <br />
              lot of screenshots
            </h2>
            <p className="saas-lede">
              Sign in with GitHub and your namespace is live in seconds. No card, no invite, no
              sales call.
            </p>
            <div className="saas-actions">
              <a className="pill pill--primary" href="/login" onClick={go("/login")}>
                Start free
              </a>
              <a className="pill pill--soft" href="/docs">
                Read the docs
              </a>
            </div>
          </div>
        </div>
      </section>

      <SaasFooter />
    </div>
  );
}

function PlanCard({ plan }: { plan: Plan }) {
  const spec = PLANS[plan];
  const featured = plan === "pro";

  return (
    <article className={`price-card${featured ? " price-card--featured" : ""}`}>
      {featured && <span className="price-card__flag">Most room</span>}

      <header className="price-card__head">
        <span className="price-card__name">{spec.label}</span>
        <p className="price-card__tagline">{spec.tagline}</p>
      </header>

      <div className="price-card__amount">
        <span className="price-card__value">${spec.price}</span>
        <span className="price-card__per">{spec.price === 0 ? "forever" : "per month"}</span>
      </div>

      <div className="price-card__headline">
        <strong>{formatPlanBytes(spec.storageBytes)}</strong> of storage
        <span>
          up to {formatPlanBytes(spec.maxFileBytes)} per file · unlimited bandwidth
        </span>
      </div>

      <ul className="price-card__list">
        {spec.features.map((feature) => (
          <li key={feature}>
            <span className="saas-kv__tick" aria-hidden="true">
              <HugeiconsIcon icon={Tick01Icon} size={13} strokeWidth={2.4} />
            </span>
            {feature}
          </li>
        ))}
      </ul>

      <a
        className={`pill pill--block ${featured ? "pill--primary" : "pill--soft"}`}
        href={featured ? `mailto:${UPGRADE_EMAIL}?subject=saucer%20Pro` : "/login"}
        onClick={featured ? undefined : go("/login")}
      >
        {featured ? "Request Pro" : "Start free"}
      </a>
    </article>
  );
}

/**
 * Enterprise as a third card, not a banner under the grid.
 *
 * A reader scanning a pricing page reads the cards and stops. With the grid
 * ending at Pro, the page said "50 GB is the ceiling" to exactly the person
 * worth the most, and a strip bolted underneath does not undo that — it is
 * below where they stopped looking. So it takes the same shape as the others:
 * a name, a price slot, a headline number, a list, a button.
 *
 * Everything listed is a commitment that can be honoured by replying to an
 * email — invoicing, a DPA, a security review, migration help. Deliberately no
 * SSO, audit log or SLA line: none of that is built, and a pricing page is the
 * worst possible place to find that out.
 */
const ENTERPRISE_FEATURES = [
  "Storage priced by volume",
  "Invoice, PO or bank transfer",
  "DPA and security review",
  "Migration help from S3 or scp",
  "A direct line, not a ticket queue",
];

function EnterpriseCard() {
  return (
    <article className="price-card price-card--ent">
      <span className="price-card__flag price-card__flag--ent">Talk to us</span>

      <header className="price-card__head">
        <span className="price-card__name">Enterprise</span>
        <p className="price-card__tagline">When 50 GB is the floor, not the ceiling.</p>
      </header>

      <div className="price-card__amount">
        <span className="price-card__value price-card__value--quote">Custom</span>
        <span className="price-card__per">billed however suits you</span>
      </div>

      <div className="price-card__headline">
        <strong>No ceiling</strong> on storage
        <span>larger single files · unlimited bandwidth, same as every plan</span>
      </div>

      <ul className="price-card__list">
        {ENTERPRISE_FEATURES.map((feature) => (
          <li key={feature}>
            <span className="saas-kv__tick" aria-hidden="true">
              <HugeiconsIcon icon={Tick01Icon} size={13} strokeWidth={2.4} />
            </span>
            {feature}
          </li>
        ))}
      </ul>

      <a
        className="pill pill--block pill--soft"
        href={`mailto:${UPGRADE_EMAIL}?subject=Enterprise%20plan`}
      >
        Email us
      </a>
    </article>
  );
}

/**
 * The add-on, as a slider rather than a table. Storage is the only thing being
 * sold, so letting someone land on their own number is the whole explanation.
 */
function Topup() {
  const [blocks, setBlocks] = useState(4);
  const bytes = blocks * PAYG_BYTES_PER_DOLLAR;

  return (
    <section className="price-topup">
      <div className="price-topup__head">
        <span className="saas-chip">Top up</span>
        <h2 className="price-topup__title">
          Need more? It is {formatPlanBytes(PAYG_BYTES_PER_DOLLAR)} for a dollar.
        </h2>
        <p className="saas-lede">
          Extra storage stacks on either plan and bills monthly alongside it. Drop it whenever —
          your links stay up, you just stop adding to them until you are back under your quota.
        </p>
      </div>

      <div className="price-topup__dial">
        <output className="price-topup__out">
          <span className="price-topup__bytes">{formatPlanBytes(bytes)}</span>
          <span className="price-topup__cost">${blocks}/month extra</span>
        </output>
        <input
          className="price-topup__range"
          type="range"
          min={1}
          max={40}
          step={1}
          value={blocks}
          onChange={(event) => setBlocks(Number(event.target.value))}
          aria-label="Extra storage, in blocks of 5 GB"
        />
        <div className="price-topup__scale meta mono">
          <span>{formatPlanBytes(PAYG_BYTES_PER_DOLLAR)}</span>
          <span>{formatPlanBytes(40 * PAYG_BYTES_PER_DOLLAR)}</span>
        </div>
        <p className="saas-micro">
          On Pro that is {formatPlanBytes(PLANS.pro.storageBytes + bytes)} in total, for $
          {PLANS.pro.price + blocks} a month.
        </p>
      </div>
    </section>
  );
}

const FAQ: { q: string; a: string }[] = [
  {
    q: "What happens when I hit my limit?",
    a: "Nothing you have already shared breaks. Every existing link keeps serving, at full speed, forever. The only thing that stops is adding new files — an upload past your quota is refused with a message telling you how far over you are. Delete something and the space comes back immediately.",
  },
  {
    q: "Is bandwidth really unlimited?",
    a: "Yes. saucer stores your files in Cloudflare R2, which charges nothing for data leaving it, so there is no per-gigabyte cost to pass on to you and no reason to meter downloads. A file that gets a hundred thousand opens costs the same as one that gets none.",
  },
  {
    q: "Why is there no card form?",
    a: "Because saucer is small and would rather not hold your card details. Upgrades are arranged by email and applied to your account by hand. It is slower than a checkout page and considerably more honest about who is on the other end.",
  },
  {
    q: "Do you delete my files if I stop paying?",
    a: "No. The account drops back to the free plan and everything stays exactly where it is — links included. You will be over quota, which means no new uploads until you delete enough to fit, but nothing is removed for you.",
  },
  {
    q: "Can I get more than the plans offer?",
    a: "Yes — top-ups stack as far as you need them to, and if you want something well past that, email and ask. Custom limits are set per account, so an unusual number is not a problem.",
  },
  {
    q: "What counts against my storage?",
    a: "The files you upload, at their real size. Thumbnails, social cards, converted previews and search indexes are on the house — they are generated from your files and are not billed back to you.",
  },
];
